import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/sub-comp/Navbar.jsx";
import Footer from "../components/sub-comp/MainFooter.jsx";
import FloatingSocialPanels from "../components/sub-comp/FloatingSocialPanels";
import cmsApi from "../services/cmsApi";
import {
  FaTrophy,
  FaMedal,
  FaUsers,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaFacebook,
  FaTwitter,
  FaInstagram,
} from "react-icons/fa";

const FALLBACK_HERO = {
  title: "JR Institute Athletics",
  subtitle: "Excellence in Sports, Character, and Academic Achievement",
  stats: [
    { label: "15+ Sports Programs", icon: "trophy" },
    { label: "50+ Championships", icon: "medal" },
    { label: "300+ Student Athletes", icon: "users" },
  ],
};

const ICON_MAP = {
  trophy: FaTrophy,
  medal: FaMedal,
  users: FaUsers,
};

const SOCIAL_ICON_MAP = {
  facebook: FaFacebook,
  twitter: FaTwitter,
  instagram: FaInstagram,
};

/** No stock photo URLs — images come from CMS (upload / URL per sport). */
const FALLBACK_PROGRAMS = [
  {
    name: "Football",
    description:
      "Our premier football program competes at the highest level with dedicated coaching and state-of-the-art facilities.",
    image: "",
    season: "",
    coach: "",
    achievements: [],
  },
  {
    name: "Basketball",
    description:
      "Fast-paced basketball program focusing on skill development, teamwork, and competitive excellence.",
    image: "",
    season: "",
    coach: "",
    achievements: [],
  },
  {
    name: "Volleyball",
    description:
      "Dynamic volleyball program emphasizing technique, strategy, and team coordination.",
    image: "",
    season: "",
    coach: "",
    achievements: [],
  },
];

const FALLBACK_NEWS = [
  {
    title: "Football Team Advances to Regional Finals",
    excerpt:
      "Our football team secured a spot in the regional finals after a thrilling victory against State University.",
    date: "2024-01-15",
    image: "",
    category: "Football",
  },
  {
    title: "Track & Field Athletes Set New Records",
    excerpt:
      "Three new school records were set at the recent track and field championship meet.",
    date: "2024-01-10",
    image: "",
    category: "Track & Field",
  },
  {
    title: "Basketball Team Hosts Annual Tournament",
    excerpt:
      "Join us for the annual basketball tournament featuring teams from across the region.",
    date: "2024-01-05",
    image: "",
    category: "Basketball",
  },
];

const FALLBACK_EVENTS = [
  { title: "Football vs. City College", date: "2024-02-15", time: "7:00 PM", location: "Home Stadium", sport: "Football" },
  { title: "Basketball Championship", date: "2024-02-20", time: "6:30 PM", location: "Sports Complex", sport: "Basketball" },
  { title: "Track & Field Meet", date: "2024-02-25", time: "9:00 AM", location: "Track Stadium", sport: "Track & Field" },
];

const FALLBACK_FACILITIES = [
  { name: "Main Stadium", description: "State-of-the-art football stadium with seating for 5,000 spectators and modern lighting system.", icon: "trophy" },
  { name: "Sports Complex", description: "Multi-purpose facility housing basketball courts, volleyball courts, and fitness center.", icon: "users" },
  { name: "Track & Field Stadium", description: "Olympic-standard track with field event areas and modern timing systems.", icon: "medal" },
  { name: "Swimming Pool", description: "Competition-sized swimming pool with starting blocks and timing systems.", icon: "swimming" },
];

const FALLBACK_CONTACT = {
  phone: "+255 754 360 590",
  email: "athletics@jriit.ac.tz",
  address: "JR Institute of Information Technology\nAthletics Department\nDar es Salaam, Tanzania",
  socialMedia: [
    { platform: "facebook", url: "https://www.facebook.com/jrinstitute" },
    { platform: "twitter", url: "https://twitter.com/JriitTechnology" },
    { platform: "instagram", url: "https://www.instagram.com/jrinstituteoftechnology/" },
  ],
  quickLinks: [
    { label: "Athletic Schedules", url: "/events" },
    { label: "Team Rosters", url: "/athletics" },
    { label: "Athletic Policies", url: "/admissions" },
    { label: "Tryout Information", url: "/admissions" },
  ],
};

const FACILITY_ICON_MAP = {
  trophy: FaTrophy,
  users: FaUsers,
  medal: FaMedal,
};

function safeParse(str, fallback) {
  if (!str) return fallback;
  try { return JSON.parse(str); } catch { return fallback; }
}

const DEFAULT_SPORTS_CARD_SETTINGS = {
  showSeason: true,
  showCoach: true,
  showAchievements: true,
};

function normalizeSportsPrograms(items) {
  if (!Array.isArray(items)) return [];
  return items.map((s) => ({
    ...s,
    image: String(s.image ?? "").trim(),
    season: String(s.season ?? "").trim(),
    coach: String(s.coach ?? "").trim(),
    achievements: Array.isArray(s.achievements) ? s.achievements.filter(Boolean) : [],
  }));
}

const Athletics = () => {
  const [hero, setHero] = useState(FALLBACK_HERO);
  const [programsMeta, setProgramsMeta] = useState({ title: "Our Sports Programs", subtitle: "Discover our comprehensive athletic programs designed to develop both physical excellence and character." });
  const [sportsPrograms, setSportsPrograms] = useState(FALLBACK_PROGRAMS);
  const [sportsCardSettings, setSportsCardSettings] = useState(DEFAULT_SPORTS_CARD_SETTINGS);
  const [newsMeta, setNewsMeta] = useState({ title: "Latest Athletics News", subtitle: "Stay updated with the latest news and achievements from our athletic programs." });
  const [recentNews, setRecentNews] = useState(FALLBACK_NEWS);
  const [eventsMeta, setEventsMeta] = useState({ title: "Upcoming Events", subtitle: "Don't miss our upcoming games and athletic events." });
  const [upcomingEvents, setUpcomingEvents] = useState(FALLBACK_EVENTS);
  const [facilitiesTitle, setFacilitiesTitle] = useState("Athletic Facilities");
  const [facilities, setFacilities] = useState(FALLBACK_FACILITIES);
  const [contactTitle, setContactTitle] = useState("Contact Athletics");
  const [contactData, setContactData] = useState(FALLBACK_CONTACT);
  /** When null, CMS returned nothing — keep static fallbacks. When set, only these sections render (public API omits inactive). */
  const [visibleSectionKeys, setVisibleSectionKeys] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const result = await cmsApi.getPageComponents("athletics");
        if (!result?.success) return;
        const list = result.components || [];
        if (list.length === 0) return;

        const map = {};
        const keys = [];
        for (const c of list) {
          map[c.componentName] = c;
          keys.push(c.componentName);
        }
        setVisibleSectionKeys(keys);

        if (map["hero"]) {
          const stats = safeParse(map["hero"].content, FALLBACK_HERO.stats);
          setHero({
            title: map["hero"].title || FALLBACK_HERO.title,
            subtitle: map["hero"].subtitle || FALLBACK_HERO.subtitle,
            stats,
          });
        }

        if (map["sports-programs"]) {
          const items = safeParse(map["sports-programs"].content, null);
          if (items?.length) setSportsPrograms(normalizeSportsPrograms(items));
          const st = safeParse(map["sports-programs"].settings, {});
          setSportsCardSettings({
            showSeason: st.showSeason !== false,
            showCoach: st.showCoach !== false,
            showAchievements: st.showAchievements !== false,
          });
          setProgramsMeta({
            title: map["sports-programs"].title || programsMeta.title,
            subtitle: map["sports-programs"].subtitle || programsMeta.subtitle,
          });
        }

        if (map["news"]) {
          const items = safeParse(map["news"].content, null);
          if (items?.length) setRecentNews(items);
          setNewsMeta({
            title: map["news"].title || newsMeta.title,
            subtitle: map["news"].subtitle || newsMeta.subtitle,
          });
        }

        if (map["events"]) {
          const items = safeParse(map["events"].content, null);
          if (items?.length) setUpcomingEvents(items);
          setEventsMeta({
            title: map["events"].title || eventsMeta.title,
            subtitle: map["events"].subtitle || eventsMeta.subtitle,
          });
        }

        if (map["facilities"]) {
          const items = safeParse(map["facilities"].content, null);
          if (items?.length) setFacilities(items);
          if (map["facilities"].title) setFacilitiesTitle(map["facilities"].title);
        }

        if (map["contact"]) {
          const data = safeParse(map["contact"].content, null);
          if (data) setContactData(data);
          if (map["contact"].title) setContactTitle(map["contact"].title);
        }
      } catch (err) {
        console.error("Failed to load athletics data from CMS:", err);
      }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showCmsSection = (key) =>
    visibleSectionKeys == null || visibleSectionKeys.includes(key);

  return (
    <div>
      <Navbar />
      <div className="mid-desktop-page">

      {/* Hero Section */}
      {showCmsSection("hero") && (
      <section className="relative bg-gradient-to-br from-custom-pink via-purple-900 to-indigo-900 text-white py-20 lg:py-32">
        <div className="absolute inset-0 bg-black/30"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
            {hero.title}
          </h1>
          <p className="text-xl sm:text-2xl mb-8 max-w-3xl mx-auto">
            {hero.subtitle}
          </p>
          <div className="flex flex-wrap justify-center gap-8 text-center">
            {hero.stats.map((s, i) => {
              const Icon = ICON_MAP[s.icon] || FaTrophy;
              return (
                <div key={i} className="flex items-center gap-2">
                  <Icon className="text-gold text-2xl" />
                  <span className="text-lg font-semibold">{s.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      )}

      {/* Sports Programs Overview */}
      {showCmsSection("sports-programs") && (
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-custom-pink mb-4">
              {programsMeta.title}
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {programsMeta.subtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {sportsPrograms.map((sport, idx) => {
              const imgUrl = String(sport.image ?? "").trim();
              const showSeason =
                sportsCardSettings.showSeason !== false &&
                String(sport.season ?? "").trim().length > 0;
              const showCoach =
                sportsCardSettings.showCoach !== false &&
                String(sport.coach ?? "").trim().length > 0;
              const achievements = Array.isArray(sport.achievements)
                ? sport.achievements.filter(Boolean)
                : [];
              const showAchievementsBlock =
                sportsCardSettings.showAchievements !== false &&
                achievements.length > 0;

              return (
                <div
                  key={idx}
                  className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
                >
                  {imgUrl ? (
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={imgUrl}
                        alt={sport.name || "Sport"}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                      <div className="absolute bottom-4 left-4">
                        <span className="bg-custom-pink text-white px-3 py-1 rounded-full text-sm font-semibold">
                          {sport.name}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="h-48 bg-gradient-to-br from-custom-pink via-purple-800 to-indigo-900 flex items-center justify-center px-6 text-center">
                      <span className="text-white text-xl font-bold drop-shadow-md">
                        {sport.name}
                      </span>
                    </div>
                  )}
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {sport.name}
                    </h3>
                    {sport.description && (
                      <p className="text-gray-600 mb-4">{sport.description}</p>
                    )}
                    {(showSeason || showCoach) && (
                      <div className="space-y-2 text-sm text-gray-500">
                        {showSeason && (
                          <div className="flex items-center gap-2">
                            <FaCalendarAlt className="text-gold shrink-0" />
                            <span>Season: {sport.season}</span>
                          </div>
                        )}
                        {showCoach && (
                          <div className="flex items-center gap-2">
                            <FaUsers className="text-gold shrink-0" />
                            <span>Coach: {sport.coach}</span>
                          </div>
                        )}
                      </div>
                    )}
                    {showAchievementsBlock && (
                      <div className="mt-4">
                        <h4 className="font-semibold text-gray-900 mb-2">
                          Recent Achievements:
                        </h4>
                        <ul className="space-y-1">
                          {achievements.map((achievement, index) => (
                            <li
                              key={index}
                              className="text-sm text-gray-600 flex items-center gap-2"
                            >
                              <FaMedal className="text-gold text-xs shrink-0" />
                              {achievement}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      )}

      {/* Recent News & Updates */}
      {showCmsSection("news") && (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-custom-pink mb-4">
              {newsMeta.title}
            </h2>
            <p className="text-lg text-gray-600">{newsMeta.subtitle}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {recentNews.map((news, idx) => {
              const newsImg = String(news.image ?? "").trim();
              return (
              <article
                key={idx}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
              >
                {newsImg ? (
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={newsImg}
                      alt={news.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-gold text-white px-3 py-1 rounded-full text-sm font-semibold">
                        {news.category}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="relative h-36 bg-gradient-to-br from-custom-pink via-purple-800 to-indigo-900 flex items-center justify-center px-6">
                    <div className="absolute top-4 left-4">
                      <span className="bg-gold text-white px-3 py-1 rounded-full text-sm font-semibold">
                        {news.category}
                      </span>
                    </div>
                    <span className="text-white text-lg font-semibold text-center line-clamp-2 mt-6">
                      {news.title}
                    </span>
                  </div>
                )}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2 hover:text-custom-pink transition-colors">
                    {news.title}
                  </h3>
                  <p className="text-gray-600 mb-4">{news.excerpt}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">{news.date}</span>
                    <a
                      href="/athletics/news"
                      className="text-custom-pink hover:text-gold font-semibold"
                    >
                      Read More &rarr;
                    </a>
                  </div>
                </div>
              </article>
            );
            })}
          </div>
        </div>
      </section>
      )}

      {/* Upcoming Events */}
      {showCmsSection("events") && (
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-custom-pink mb-4">
              {eventsMeta.title}
            </h2>
            <p className="text-lg text-gray-600">{eventsMeta.subtitle}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {upcomingEvents.map((event, idx) => (
              <div
                key={idx}
                className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-custom-pink text-white p-3 rounded-full">
                    <FaCalendarAlt className="text-xl" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">
                      {event.title}
                    </h3>
                    <span className="text-sm text-gold font-semibold">
                      {event.sport}
                    </span>
                  </div>
                </div>
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <FaCalendarAlt className="text-gold" />
                    <span>{event.date}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-gold">⏰</span>
                    <span>{event.time}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <FaMapMarkerAlt className="text-gold" />
                    <span>{event.location}</span>
                  </div>
                </div>
                <Link to="/contact" className="block w-full mt-4 bg-gold text-white py-2 px-4 rounded-lg hover:bg-custom-pink transition-colors duration-300 text-center">
                  Get Tickets
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Facilities & Contact */}
      {(showCmsSection("facilities") || showCmsSection("contact")) && (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className={`grid gap-12 ${
              showCmsSection("facilities") && showCmsSection("contact")
                ? "lg:grid-cols-2"
                : "lg:grid-cols-1 max-w-3xl mx-auto"
            }`}
          >
            {/* Facilities */}
            {showCmsSection("facilities") && (
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-custom-pink mb-6">
                {facilitiesTitle}
              </h2>
              <div className="space-y-6">
                {facilities.map((facility, idx) => {
                  const FacIcon = FACILITY_ICON_MAP[facility.icon];
                  return (
                    <div key={idx} className="flex items-start gap-4">
                      <div className="bg-gold text-white p-3 rounded-full">
                        {FacIcon ? (
                          <FacIcon className="text-xl" />
                        ) : (
                          <span className="text-xl">🏊</span>
                        )}
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">
                          {facility.name}
                        </h3>
                        <p className="text-gray-600">{facility.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            )}

            {/* Contact Information */}
            {showCmsSection("contact") && (
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-custom-pink mb-6">
                {contactTitle}
              </h2>
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="bg-custom-pink text-white p-3 rounded-full">
                    <FaPhone className="text-xl" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Phone</h3>
                    <p className="text-gray-600">{contactData.phone}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="bg-custom-pink text-white p-3 rounded-full">
                    <FaEnvelope className="text-xl" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Email</h3>
                    <p className="text-gray-600">{contactData.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="bg-custom-pink text-white p-3 rounded-full">
                    <FaMapMarkerAlt className="text-xl" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Address</h3>
                    <p className="text-gray-600 whitespace-pre-line">
                      {contactData.address}
                    </p>
                  </div>
                </div>
              </div>

              {/* Social Media */}
              <div className="mt-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  Follow Us
                </h3>
                <div className="flex gap-4">
                  {contactData.socialMedia.map((social, idx) => {
                    const SIcon = SOCIAL_ICON_MAP[social.platform];
                    if (!SIcon) return null;
                    return (
                      <a
                        key={idx}
                        href={social.url}
                        target="_blank"
                        rel="noreferrer"
                        className="bg-custom-pink text-white p-3 rounded-full hover:bg-gold transition-colors duration-300"
                        aria-label={social.platform}
                      >
                        <SIcon className="text-xl" />
                      </a>
                    );
                  })}
                </div>
              </div>

              {/* Quick Links */}
              <div className="mt-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  Quick Links
                </h3>
                <div className="space-y-2">
                  {contactData.quickLinks.map((link, idx) => (
                    <Link
                      key={idx}
                      to={link.url}
                      className="block text-custom-pink hover:text-gold transition-colors duration-300"
                    >
                      &rarr; {link.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
            )}
          </div>
        </div>
      </section>
      )}

      </div>

      <Footer />
      <FloatingSocialPanels />
    </div>
  );
};

export default Athletics;
